import React, { useState } from 'react'

import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Paper,
    Divider,
    useTheme,
    IconButton,
    Box,
    Chip,
} from '@material-ui/core'
import { Close as CloseIcon } from '@material-ui/icons'

import { PaymentSchedule } from '../../../models/Billing/types'
import { CompanyWithInvoiceCode } from '../Property/types'
import { CompanyStatus } from '../../../models'
import { CreateBillingGroupRequest } from '../../../hooks/useBillingGroups'
import { Selector } from '../../../components/Selector'

interface CreateBillingGroupModalProps {
    open: boolean
    propertyList: CompanyWithInvoiceCode[]
    onClose: () => void
    onCreate: (data: CreateBillingGroupRequest) => void
}

export const CreateBillingGroupModal: React.FC<CreateBillingGroupModalProps> = ({
    open,
    propertyList,
    onClose,
    onCreate,
}) => {
    const theme = useTheme()
    const [customerName, setCustomerName] = useState('')
    const [invoiceEmail, setInvoiceEmail] = useState('')
    const [paymentSchedule, setPaymentSchedule] = useState<PaymentSchedule>(
        PaymentSchedule.MONTHLY,
    )
    const [selectedApartments, setSelectedApartments] = useState<
        CompanyWithInvoiceCode[]
    >([])
    const [propertySearchTerm, setPropertySearchTerm] = useState('')
    const [isSaving, setIsSaving] = useState(false)

    // Get available apartments (Active, no billing group)
    const availableApartments = React.useMemo(() => {
        return propertyList.filter(
            (property) =>
                property.status === CompanyStatus.ACTIVE &&
                property.billing_group === null,
        )
    }, [propertyList])

    // Filter available apartments based on organization of first selected apartment
    const filteredAvailableApartments = React.useMemo(() => {
        if (selectedApartments.length === 0) {
            return availableApartments
        }

        const firstApartmentOrgId = selectedApartments[0].organization.id
        return availableApartments.filter(
            (apartment) => apartment.organization.id === firstApartmentOrgId,
        )
    }, [availableApartments, selectedApartments])

    const handlePropertySelect = (
        event: React.ChangeEvent<{ value: unknown }>,
    ) => {
        const propertyIds = event.target.value as number[]
        const newSelectedProperties = propertyIds
            .map((id) => filteredAvailableApartments.find((p) => p.id === id))
            .filter(Boolean) as CompanyWithInvoiceCode[]

        setSelectedApartments(newSelectedProperties)
    }

    const handleRemoveApartment = (apartmentId: number) => {
        setSelectedApartments(
            selectedApartments.filter((apt) => apt.id !== apartmentId),
        )
    }

    const handleCreate = async () => {
        if (
            customerName.trim() &&
            invoiceEmail.trim() &&
            selectedApartments.length > 0
        ) {
            setIsSaving(true)
            try {
                const createData: CreateBillingGroupRequest = {
                    customer_name: customerName.trim(),
                    invoice_email: invoiceEmail.trim(),
                    payment_schedule: paymentSchedule,
                    apartment_ids: selectedApartments.map((apt) => apt.id),
                }
                await onCreate(createData)
                handleClose()
            } catch (error) {
                // Error is handled in the parent component
                console.error('Failed to create billing group:', error)
            } finally {
                setIsSaving(false)
            }
        }
    }

    const handleClose = () => {
        setCustomerName('')
        setInvoiceEmail('')
        setPaymentSchedule(PaymentSchedule.MONTHLY)
        setSelectedApartments([])
        setPropertySearchTerm('')
        onClose()
    }

    const isFormValid =
        customerName.trim() &&
        invoiceEmail.trim() &&
        selectedApartments.length > 0 &&
        !isSaving

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: 12,
                    maxHeight: '90vh',
                },
            }}
        >
            <Box
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: theme.spacing(1, 3),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.default,
                }}
            >
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                    Create New Billing Group
                </Typography>
                <IconButton
                    onClick={handleClose}
                    style={{ color: theme.palette.text.secondary }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent style={{ padding: theme.spacing(3) }}>
                <div style={{ marginBottom: theme.spacing(3) }}>
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            color: theme.palette.primary.main,
                        }}
                    >
                        Billing Information
                    </Typography>

                    <div
                        style={{
                            display: 'flex',
                            gap: theme.spacing(3),
                            marginBottom: theme.spacing(2),
                        }}
                    >
                        <TextField
                            label="Quickbooks Customer Name"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            variant="outlined"
                            fullWidth
                            required
                        />
                    </div>

                    <div style={{ display: 'flex', gap: theme.spacing(3) }}>
                        <TextField
                            label="Invoice Email"
                            value={invoiceEmail}
                            onChange={(e) => setInvoiceEmail(e.target.value)}
                            variant="outlined"
                            fullWidth
                            required
                            type="email"
                        />

                        <FormControl
                            variant="outlined"
                            style={{ minWidth: '200px' }}
                        >
                            <InputLabel>Payment Schedule</InputLabel>
                            <Select
                                value={paymentSchedule}
                                onChange={(e) =>
                                    setPaymentSchedule(
                                        e.target.value as PaymentSchedule,
                                    )
                                }
                                label="Payment Schedule"
                            >
                                <MenuItem value={PaymentSchedule.MONTHLY}>
                                    Monthly
                                </MenuItem>
                                <MenuItem value={PaymentSchedule.ANNUALLY}>
                                    Annually
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                </div>

                <Divider style={{ margin: theme.spacing(2, 0) }} />

                <div>
                    <Typography
                        variant="h6"
                        style={{
                            fontWeight: 600,
                            marginBottom: theme.spacing(2),
                            color: theme.palette.primary.main,
                        }}
                    >
                        Select Properties ({selectedApartments.length} selected)
                    </Typography>

                    {selectedApartments.length > 0 && (
                        <div style={{ marginBottom: theme.spacing(2) }}>
                            <Typography
                                variant="body2"
                                style={{
                                    marginBottom: theme.spacing(1),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Selected Properties:
                            </Typography>
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: theme.spacing(1),
                                    maxHeight: '120px',
                                    overflowY: 'auto',
                                    padding: theme.spacing(1),
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    borderRadius: theme.spacing(1),
                                    backgroundColor: theme.palette.grey[50],
                                }}
                            >
                                {selectedApartments.map((apartment) => (
                                    <Chip
                                        key={apartment.id}
                                        label={apartment.name}
                                        onDelete={() =>
                                            handleRemoveApartment(apartment.id)
                                        }
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {selectedApartments.length > 0 && (
                        <Typography
                            variant="body2"
                            style={{
                                marginBottom: theme.spacing(2),
                                color: theme.palette.info.main,
                                fontStyle: 'italic',
                            }}
                        >
                            Only properties from the same organization (
                            {selectedApartments[0].organization.name}) can be
                            added to this billing group.
                        </Typography>
                    )}

                    <Paper
                        style={{
                            padding: theme.spacing(2),
                            backgroundColor: theme.palette.grey[50],
                            border: `1px solid ${theme.palette.grey[200]}`,
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                gap: theme.spacing(2),
                                alignItems: 'flex-end',
                            }}
                        >
                            <Selector
                                multiple={true}
                                label="Select Property"
                                currentValue={selectedApartments.map(
                                    (apt) => apt.id,
                                )}
                                onChange={handlePropertySelect}
                                data={filteredAvailableApartments}
                                getDisplayString={(property) => property.name}
                                searchable={true}
                                disabled={
                                    filteredAvailableApartments.length === 0
                                }
                                popupHeight={300}
                                customRenderValue={() => (
                                    <span
                                        style={{
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        Choose a property to add...
                                    </span>
                                )}
                                customStyle={{
                                    formControl: {
                                        width: '100%',
                                    },
                                }}
                            />
                        </div>

                        <Typography
                            variant="body2"
                            style={{
                                marginTop: theme.spacing(1),
                                color: theme.palette.text.secondary,
                                fontSize: '12px',
                            }}
                        >
                            {filteredAvailableApartments.length === 0
                                ? selectedApartments.length > 0
                                    ? 'No more properties available from this organization.'
                                    : 'No properties available. All properties must be active and not already assigned to a billing group.'
                                : `${filteredAvailableApartments.length} properties available.`}
                        </Typography>
                    </Paper>
                </div>
            </DialogContent>

            <DialogActions
                style={{
                    padding: theme.spacing(2, 3),
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.default,
                }}
            >
                <Button
                    onClick={handleClose}
                    color="default"
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleCreate}
                    color="primary"
                    variant="contained"
                    disabled={!isFormValid}
                    style={{
                        minWidth: '120px',
                    }}
                >
                    {isSaving ? 'Creating...' : 'Create Billing Group'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
